import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import FormDialog from 'src/components/DialogBox';
import EditVendorStreamDialog from './EditPhysicalServerDialog';
import AddVendorStreamDialog from './AddPhysicalServerDialog';
import { authenticationService } from 'src/services/authentication.service';
import { deleteEntry, get,post, put } from 'src/services/httpClient.service';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';
import { hasPermission } from 'src/components/Permissions/hasPermission';

interface VendorStream {
  id: string;
  vendorId: string;
  url: string;
  mediaServerUrl: string;
  publicUrl: string;
  name: string;
  streamEngine:string;
  channelPort: string;
  galleryPort: string;
  galleryUploadPort: string;
  apiPort: string;
  channelsCount: number;
  createdDate: string;
  status: 'ACTIVE' | 'INACTIVE';
  urlStatus?: string;
  mediaServerStatus?: string;
  publicUrlStatus?: string;
  serverType?:string;
  isActive?:Boolean;
}

//  const API_URL = 'http://localhost:8080/api/vendor-streams/a3dec822-f20d-46f1-b9df-0d9c33b13cf6';

const OTTPhysicalServer = () => {
  const [streams, setStreams] = useState<VendorStream[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedStream, setSelectedStream] = useState<VendorStream | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'info' | 'warning' | 'error',
  });
  const { showSnackbar } = useSnackbar();

  const {vendorId, franchiseId, companyId, operatorId} = authenticationService.getVendorFranchiseIds();

  const fetchStreams = async () => {
    try {
      setLoading(true);
      console.log("VendorId ::"+vendorId)
      const response = await get(`${process.env.REACT_APP_BASE_API_URL}/api/vendor-streams/vendor/${vendorId}/findAll`);
      // Adding mock status since the API might not return it
      const streamsWithStatus = response?.data.map((stream: VendorStream) => ({
        ...stream
      }));
      setStreams(streamsWithStatus);
      setError('');
    } catch (err) {
      console.error('Error fetching vendor streams:', err);
      showSnackbar('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate random status (for demo purposes)
  const getRandomStatus = (): 'active' | 'inactive' | 'maintenance' => {
    const statuses: ('active' | 'inactive' | 'maintenance')[] = ['active', 'inactive', 'maintenance'];
    return statuses[Math.floor(Math.random() * statuses.length)];
  };

  const handleEditClick = (stream: VendorStream) => {
    setSelectedStream(stream);
    setOpenUpdateDialog(true);
  };

  const handleDeleteClick = (stream: VendorStream) => {
    setSelectedStream(stream);
    setDeleteDialogOpen(true);
  };

  useEffect(() => {
    fetchStreams();
  }, []);

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    if (!selectedStream) return;

    try {
      await deleteEntry(`${process.env.REACT_APP_BASE_API_URL}/api/vendor-streams/${selectedStream.id}`);
      showSnackbar('Stream deleted successfully!', 'success');
      fetchStreams();
    } catch (err) {
      console.error('Error deleting stream:', err);
      showSnackbar( 'Failed to delete the stream.', 'error');
    }
  };

  const handleAddStreamSubmit = async (newStreamData: any/* Omit<VendorStream, 'id' | 'createdDate'> */) => {
    try {
      const response = await post(`${process.env.REACT_APP_BASE_API_URL}/api/vendor-streams`, newStreamData);
      showSnackbar('Stream added successfully!', 'success');
      setOpenDialog(false);
      fetchStreams(); // Refresh table
    } catch (err) {
      console.error('Error adding stream:', err);
      showSnackbar('Failed to add the stream.', 'error');
    }
  };

  const handleUpdateStreamSubmit = async (updatedStreamData: VendorStream) => {
    if (!selectedStream) return;
    
    try {
      await put(`${process.env.REACT_APP_BASE_API_URL}/api/vendor-streams/${selectedStream.id}`, updatedStreamData);
     showSnackbar('Stream updated successfully!', 'success');
      setOpenUpdateDialog(false);
      fetchStreams();
    } catch (err) {
      console.error('Error updating stream:', err);
      showSnackbar('Failed to update the stream.', 'error');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ONLINE':
        return 'success';
      case 'OFFLINE':
      case 'ERROR':
      case 'NOT_AVAILABLE':
        return 'error';
      case 'TIMEOUT':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <>
      <Helmet>
        <title>Stream Servers</title>
      </Helmet>
      
      <Box sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h4" component="h1">
            Stream Servers
          </Typography>
        {
            hasPermission(["OWNER", "server.create"]) && (<Button
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Add Stream Server
            </Button>)
        }

        </Box>

        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ color: 'error.main', textAlign: 'center', p: 3 }}>
              {error}
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Stream</TableCell>
                  
                  {/* <TableCell>Server Type</TableCell> */}
                  <TableCell>Server Status</TableCell>
                  
                  
                  
                  {/* <TableCell>Channel Port</TableCell>
                  <TableCell>Gallery Port</TableCell>
                  <TableCell>Gallery Upload Port</TableCell>
                  <TableCell>API Port</TableCell> */}
                  {/* <TableCell>Channels</TableCell> */}
                  {/* <TableCell>Live Status</TableCell> */}
                  {/* <TableCell>Created Date</TableCell> */}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {streams.map((stream) => (
                  <TableRow key={stream.id}>
                    <TableCell>{stream.name}</TableCell>
                    <TableCell>{stream.url}</TableCell>
                    <TableCell>{stream.streamEngine}</TableCell>
                    
                    {/* <TableCell>{stream.serverType}</TableCell> */}
                    <TableCell>
                      <Chip 
                        label={stream.urlStatus} 
                        color={getStatusColor(stream.urlStatus)} 
                        size="small"
                      />
                    </TableCell>
                    {/* <TableCell>{stream.channelPort}</TableCell>
                    <TableCell>{stream.galleryPort}</TableCell>
                    <TableCell>{stream.galleryUploadPort}</TableCell>
                    <TableCell>{stream.apiPort}</TableCell> */}
                    {/* <TableCell>{stream.channelsCount}</TableCell> */}
                   {/* <TableCell>
  <Chip
    label={
      stream.isActive === true
        ? "Running"
        : stream.isActive === false
        ? "Stopped"
        : "N/A"
    }
    color={
      stream.isActive === true
        ? "success"
        : stream.isActive === false
        ? "error"
        : "default"
    }
    size="small"
  />
</TableCell> */}
                    {/* <TableCell
                      style={{
                        color: stream.status === "ACTIVE" ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {stream.status}
                    </TableCell> */}
                    {/* <TableCell>{new Date(stream.createdDate).toLocaleString()}</TableCell> */}
                    <TableCell>
                    {
                      hasPermission(["OWNER", "server.edit"]) && 
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEditClick(stream)}
                      >
                        <EditIcon />
                      </IconButton>
                    } 
                    {
                      hasPermission(["OWNER", "server.delete"]) &&
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteClick(stream)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Add Stream Dialog */}
        {openDialog && (
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>Add Vendor Stream</DialogTitle>
            <AddVendorStreamDialog
              onSubmit={handleAddStreamSubmit}
              onClose={() => setOpenDialog(false)}
            />
          </Dialog>
        )}

        {/* Edit Stream Dialog */}
        {openUpdateDialog && selectedStream && (
          <Dialog open={openUpdateDialog} onClose={() => setOpenUpdateDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>Edit Vendor Stream</DialogTitle>
            <EditVendorStreamDialog
              initialData={selectedStream}
              onSubmit={handleUpdateStreamSubmit}
              onClose={() => setOpenUpdateDialog(false)}
            />
          </Dialog>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            Are you sure you want to delete stream "{selectedStream?.name}"?
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialogOpen(false)} 
              color="primary"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ fontWeight: 'bold' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default OTTPhysicalServer; 